import axiosInstance, { apiWithContext } from './api';

// Service Products API Endpoints
export interface ServiceProduct {
  _id: string;
  barcode: string;
  name: string;
  status: 'active' | 'inactive';
  price: number;
  financialAccount: string;
  financialAccountName: string;
  financialAccountCode: string;
  serviceFee: number;
  notes?: string;
  // حقول الضريبة
  tax?: string; // معرف الضريبة
  taxName?: string; // اسم الضريبة
  taxRate?: number; // معدل الضريبة
  // حقول التصنيف الافتراضي
  defaultCategory?: { _id: string; name: string; returnPeriod?: number } | string;
  defaultCategoryName?: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  updatedBy?: string;
}

export interface ServiceProductsResponse {
  success: boolean;
  data: ServiceProduct[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface ServiceProductResponse {
  success: boolean;
  data: ServiceProduct;
}

// الحصول على جميع خدمات المنتجات
export const getServiceProducts = async (params?: {
  search?: string;
  status?: string;
  page?: number;
  limit?: number;
  sort?: string;
  order?: string;
}): Promise<ServiceProductsResponse> => {
  try {
    // استخدام السياق 'invoice' للسماح بالوصول إلى خدمات المنتجات من صفحة الفواتير
    const response = await apiWithContext.get('/service-products', 'invoice', { params });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching service products:', error);
    throw error;
  }
};

// البحث عن خدمة منتج بالباركود
export const getServiceProductByBarcode = async (barcode: string): Promise<ServiceProductResponse> => {
  try {
    // استخدام السياق 'invoice' للسماح بالوصول إلى خدمات المنتجات من صفحة الفواتير
    const response = await apiWithContext.get(`/service-products/barcode/${barcode}`, 'invoice');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching service product by barcode:', error);
    throw error;
  }
};

// الحصول على خدمة منتج بالمعرف
export const getServiceProductById = async (id: string): Promise<ServiceProductResponse> => {
  try {
    // استخدام السياق 'invoice' للسماح بالوصول إلى خدمات المنتجات من صفحة الفواتير
    const response = await apiWithContext.get(`/service-products/${id}`, 'invoice');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching service product by id:', error);
    throw error;
  }
};

// إنشاء خدمة منتج جديدة
export const createServiceProduct = async (data: Omit<ServiceProduct, '_id' | 'createdAt' | 'updatedAt'>): Promise<ServiceProductResponse> => {
  try {
    const response = await axiosInstance.post('/service-products', data);
    return response.data;
  } catch (error: any) {
    console.error('Error creating service product:', error);
    throw error;
  }
};

// تحديث خدمة منتج
export const updateServiceProduct = async (id: string, data: Partial<ServiceProduct>): Promise<ServiceProductResponse> => {
  try {
    const response = await axiosInstance.put(`/service-products/${id}`, data);
    return response.data;
  } catch (error: any) {
    console.error('Error updating service product:', error);
    throw error;
  }
};

// حذف خدمة منتج
export const deleteServiceProduct = async (id: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axiosInstance.delete(`/service-products/${id}`);
    return response.data;
  } catch (error: any) {
    console.error('Error deleting service product:', error);
    throw error;
  }
};

// Service Product Operations API Endpoints
export interface ServiceProductOperationCreate {
  serviceProductId: string;
  quantity: number;
  date: string;
  branchId: string;
  referenceNumber?: string;
  transactionType?: 'sale' | 'return' | 'other';
  notes?: string;
}

export interface ServiceProductOperationResponse {
  success: boolean;
  data: any;
}

// إنشاء عملية خدمة منتج جديدة
export const createServiceProductOperation = async (data: ServiceProductOperationCreate): Promise<ServiceProductOperationResponse> => {
  try {
    const response = await axiosInstance.post('/service-product-operations', data);
    return response.data;
  } catch (error: any) {
    console.error('Error creating service product operation:', error);
    throw error;
  }
};